import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Container, Drawer, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../App.css";
import FeaturedCategories from "../components/FeaturedCategories";
import Filters from "../components/Filters";
import HeroSection from "../components/HeroSection";
import Products from "../components/Products";
import {
  loadFilters,
  loadSort,
  saveFilters,
  saveSort,
} from "../utils/navState";

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (text === "Spandex Products") return "Spandex";
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

function Home({ selectedCategory, setSelectedCategory = () => {} }) {
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState(() =>
    loadFilters(selectedCategory)
  );
  const [selectedSort, setSelectedSort] = useState(() =>
    loadSort(selectedCategory)
  );
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productsCount, setProductsCount] = useState(0);
  const [categoryBannerImage, setCategoryBannerImage] = useState("");
  // Reserve the banner's layout slot on the very first render when a category
  // is active, so the document height is already correct before scroll-restore
  // runs. Without this, the banner appears via a follow-up effect, the page
  // grows by ~540px, and the browser's scroll anchor shifts the saved position.
  const [categoryBannerLoading, setCategoryBannerLoading] = useState(
    () => !!selectedCategory
  );
  const [categoryName, setCategoryName] = useState("");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    handleResize(); // Set initial isMobile state
    window.addEventListener("resize", handleResize); // Listen for resize events
    return () => {
      window.removeEventListener("resize", handleResize); // Clean up event listener
    };
  }, []);

  // Reload persisted filters whenever the active category changes.
  useEffect(() => {
    setSelectedFilters(loadFilters(selectedCategory));
    setSelectedSort(loadSort(selectedCategory));
  }, [selectedCategory]);

  const handleFiltersChange = (filters) => {
    setSelectedFilters(filters);
    if (selectedCategory) saveFilters(selectedCategory, filters);
  };

  const handleSortChange = (sort) => {
    setSelectedSort(sort);
    if (selectedCategory) saveSort(selectedCategory, sort);
  };

  const clearFilters = () => {
    setSelectedFilters({});
    if (selectedCategory) saveFilters(selectedCategory, {});
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  useEffect(() => {
    setCategoryBannerImage("");
    setCategoryName("");
    if (selectedCategory) {
      setCategoryBannerLoading(true);
    }
  }, [selectedCategory]);

  const handleCategoryBannerChange = (image) => {
    setCategoryBannerImage(image);
    setCategoryBannerLoading(false);
  };

  return (
    <>
      {!selectedCategory && <HeroSection />}
      <Container
        sx={{
          padding: {
            xs: "20px 12px",
            sm: "30px 16px !important",
            md: "30px 60px !important",
          },
          maxWidth: "1440px !important",
        }}>
        {/* Hero Section - Video or Image Slider */}

        {/* Featured Categories Section */}
        {!selectedCategory && (
          <Box sx={{ mb: 5 }}>
            <FeaturedCategories onSelectCategory={setSelectedCategory} />
          </Box>
        )}

        {/* Products and Filters Section */}
        {selectedCategory && (
          <>
            {(categoryBannerLoading || categoryBannerImage) && (
              <Box
                key={selectedCategory}
                sx={{
                  width: "100%",
                  mb: { xs: 3, md: 10 },
                  overflow: "hidden",
                }}>
                {categoryBannerLoading ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "540px",
                      background:
                        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite",
                      "@keyframes shimmer": {
                        "0%": { backgroundPosition: "200% 0" },
                        "100%": { backgroundPosition: "-200% 0" },
                      },
                    }}
                  />
                ) : (
                  <img
                    src={categoryBannerImage}
                    alt={`${selectedCategory} banner`}
                    style={{
                      width: "100%",
                      height: "540px",
                      display: "block",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
            )}
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "24px", md: "45px" },
                lineHeight: { xs: "32px", md: "50px" },
                color: "#000000",
                letterSpacing: "-2%",
                fontWeight: 500,
                marginTop: { xs: "20px", md: "0" },
                fontFamily: "Avenir Book",
                mb: 3,
              }}>
              {categoryName
                ? decodeHtmlEntities(categoryName)
                : selectedCategory
                    .replace(/-/g, " ")
                    .split(" ")
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ")}
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              alignItems={{ xs: "start", md: "center" }}
              mb={{ xs: 3, md: 4 }}>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#7D7D7D",
                    letterSpacing: "-2%",
                    fontWeight: 400,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}>
                  <Typography
                    component="span"
                    onClick={() => {
                      setSelectedCategory("");
                      navigate("/");
                    }}
                    sx={{
                      color: "#7D7D7D",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "20px",
                      cursor: "pointer",
                      fontFamily: "Avenir Book",
                      "&:hover": { color: "#000000" },
                    }}>
                    Category
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#000000",
                      fontSize: "16px",
                      lineHeight: "20px",
                      fontWeight: 400,
                      fontFamily: "Avenir Book",
                    }}>
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2087_1613)">
                        <path
                          d="M3.38288 9.62958C3.60747 9.85416 3.96955 9.85416 4.19413 9.62958L8.00288 5.82083C8.18163 5.64208 8.18163 5.35333 8.00288 5.17458L4.19413 1.36583C3.96955 1.14124 3.60747 1.14124 3.38288 1.36583C3.1583 1.59041 3.1583 1.95249 3.38288 2.17708L6.70122 5.49999L3.3783 8.82291C3.1583 9.04291 3.1583 9.40958 3.38288 9.62958Z"
                          fill="#7D7D7D"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2087_1613">
                          <rect width="11" height="11" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#000000",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "20px",
                      fontFamily: "Avenir Book",
                    }}>
                    {categoryName
                ? decodeHtmlEntities(categoryName)
                : selectedCategory
                    .replace(/-/g, " ")
                    .split(" ")
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ")}
                  </Typography>
                </Typography>
              </Box>
              {/* <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "16px",
                    lineHeight: "20px",
                    color: "#000000",
                    letterSpacing: "-2%",
                    fontWeight: 400,
                  }}>
                  {productsCount}{" "}
                  {productsCount === 1 ? (
                    <Typography
                      variant="span"
                      sx={{
                        color: "#7D7D7D",
                      }}>
                      product
                    </Typography>
                  ) : (
                    <Typography
                      variant="span"
                      sx={{
                        color: "#7D7D7D",
                      }}>
                      products
                    </Typography>
                  )}
                </Typography>
              </Box> */}

              <Box width={{ xs: "100%", md: "auto" }}>
                <Drawer
                  sx={{
                    "& .MuiDrawer-paper": {
                      maxWidth: "450px",
                      width: "100%",
                      py: 2,
                      boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                  variant="temporary"
                  anchor="right"
                  open={drawerOpen}
                  onClose={toggleDrawer}
                  ModalProps={{ keepMounted: true }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      borderBottom: "1px solid #2B2B2B2E",
                      pb: 2,
                      px: 2,
                    }}>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        lineHeight: "24px",
                        color: "#2B2B2B",
                        fontWeight: 400,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontFamily: "Avenir Book",
                      }}>
                      <FilterListIcon />
                      Filter & Sort
                    </Typography>
                    <IconButton
                      onClick={toggleDrawer}
                      sx={{ color: "#2B2B2B" }}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Filters
                    selectedCategory={selectedCategory}
                    filters={selectedFilters}
                    onFiltersChange={handleFiltersChange}
                    clearFilters={clearFilters}
                    filterPanelOpen={drawerOpen}
                    selectedSort={selectedSort}
                    onSortChange={handleSortChange}
                  />
                </Drawer>
                <Box
                  onClick={toggleDrawer}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    gap: 1.5,
                    cursor: "pointer",
                  }}>
                  <IconButton sx={{ padding: 0, color: "#2B2B2B" }}>
                    <FilterListIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                  <Typography
                    sx={{
                      color: "#2B2B2B",
                      fontSize: "16px",
                      lineHeight: "20px",
                      fontWeight: 400,
                      letterSpacing: "-2%",
                      fontFamily: "Avenir Book",
                    }}>
                    FILTER & SORT
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Products
              selectedCategory={selectedCategory}
              selectedFilters={selectedFilters}
              selectedSort={selectedSort}
              clearFilters={clearFilters}
              setSelectedCategory={setSelectedCategory}
              onProductsCountChange={setProductsCount}
              onCategoryBannerChange={handleCategoryBannerChange}
              onCategoryNameChange={setCategoryName}
            />

            {/* </Grid> */}
          </>
        )}
      </Container>
    </>
  );
}

export default Home;
